import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, isValid } from 'date-fns';
import { posApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import GroupByToggle from '@/components/reports/GroupByToggle';
import Pagination, { LIST_PAGE_SIZE } from '@/components/common/Pagination';
import ReportExportButton from '@/components/reports/ReportExportButton';
import { downloadCsv } from '@/utils/csv';
import { formatMoney } from '@/utils/currency';

const formatDateSafe = (dateStr: string | null | undefined, formatStr: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isValid(d) ? format(d, formatStr) : '—';
};

export default function ReturnedInvoicesSection() {
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [groupBy, setGroupBy] = useState<'Invoice' | 'Item'>('Invoice');
  const [page, setPage] = useState(0);

  const { data: returnsData, isLoading } = useQuery({
    // Ensure queryKey values are always defined strings to avoid React Query internal errors
    queryKey: [
      'returnedInvoices',
      from_date ? String(from_date) : '',
      to_date ? String(to_date) : '',
      groupBy,
    ],
    queryFn: async () => {
      const res = await posApi.getReturnedInvoices(from_date, to_date, groupBy);
      return res.data.message || [];
    },
    // Only run the query when both dates are provided
    enabled: Boolean(from_date && to_date),
  });

  const summaryMetrics = useMemo(() => {
    if (!Array.isArray(returnsData) || returnsData.length === 0) return { totalReturned: 0, itemsReturned: 0, transactions: 0 };
    
    let totalReturned = 0;
    let itemsReturned = 0;
    const transactions = returnsData.length;

    returnsData.forEach((row: any) => {
      if (groupBy === 'Invoice') {
        totalReturned += Number(row.refund_amount || 0);
      } else {
        totalReturned += Number(row.returned_amount || 0);
        itemsReturned += Number(row.returned_qty || 0);
      }
    });

    return { totalReturned, itemsReturned, transactions };
  }, [returnsData, groupBy]);

  const currentData = returnsData?.slice(page * LIST_PAGE_SIZE, (page + 1) * LIST_PAGE_SIZE) || [];

  const handleExport = () => {
    if (!Array.isArray(returnsData) || returnsData.length === 0) return;
    
    if (groupBy === 'Invoice') {
      const csvData = returnsData.map((row: any) => ({
        invoice_id: row.invoice_id,
        return_date: formatDateSafe(row.return_date, 'MMM d, yyyy h:mm a'),
        customer: row.customer_name || 'Walk in',
        total_items: row.total_items || 0,
        returned_items: row.returned_items || 0,
        total_bill: row.total_bill || 0,
        returned_amount: row.refund_amount || 0,
        remaining_amount: row.remaining_amount || 0,
      }));
      downloadCsv(`Returned_Invoices_${from_date}_to_${to_date}.csv`, [
        { key: 'invoice_id', label: 'Invoice ID' },
        { key: 'return_date', label: 'Return Date' },
        { key: 'customer', label: 'Customer' },
        { key: 'total_items', label: 'Total Items' },
        { key: 'returned_items', label: 'Returned Items' },
        { key: 'total_bill', label: 'Total Bill' },
        { key: 'returned_amount', label: 'Returned Amount' },
        { key: 'remaining_amount', label: 'Remaining Amount' },
      ], csvData);
    } else {
      const csvData = returnsData.map((row: any) => ({
        item_name: row.item_name,
        sold_qty: row.sold_qty || 0,
        returned_qty: row.returned_qty || 0,
        returned_amount: row.returned_amount || 0,
      }));
      downloadCsv(`Returned_Items_${from_date}_to_${to_date}.csv`, [
        { key: 'item_name', label: 'Item Name' },
        { key: 'sold_qty', label: 'Sold Qty' },
        { key: 'returned_qty', label: 'Returned Qty' },
        { key: 'returned_amount', label: 'Returned Amount' },
      ], csvData);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col xl:flex-row gap-4 items-start xl:items-center justify-between bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
        <ReportDateRangeFilter
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          customFrom={customFrom}
          onCustomFromChange={setCustomFrom}
          customTo={customTo}
          onCustomToChange={setCustomTo}
        />
        <div className="flex flex-wrap items-center gap-3">
          <GroupByToggle
            groupBy={groupBy}
            onChange={(val) => {
              setGroupBy(val as 'Invoice' | 'Item');
              setPage(0);
            }}
          />
          <ReportExportButton onClick={handleExport} disabled={!returnsData?.length} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Returned Amount</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatMoney(summaryMetrics.totalReturned)}
          </p>
        </div>
        {groupBy === 'Item' && (
          <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
            <p className="text-sm font-medium text-gray-500 mb-1">Items Returned</p>
            <p className="text-2xl font-bold text-gray-900">{summaryMetrics.itemsReturned}</p>
          </div>
        )}
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Records</p>
          <p className="text-2xl font-bold text-gray-900">{summaryMetrics.transactions}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col">
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              {groupBy === 'Invoice' ? (
                <tr>
                  <th className="p-4 font-medium text-gray-600">Invoice ID</th>
                  <th className="p-4 font-medium text-gray-600">Return Date</th>
                  <th className="p-4 font-medium text-gray-600">Customer</th>
                  <th className="p-4 font-medium text-gray-600 text-right">Total Items</th>
                  <th className="p-4 font-medium text-gray-600 text-right">Returned Items</th>
                  <th className="p-4 font-medium text-gray-600 text-right">Total Bill</th>
                  <th className="p-4 font-medium text-gray-600 text-right">Returned Amount</th>
                  <th className="p-4 font-medium text-gray-600 text-right">Remaining Amount</th>
                </tr>
              ) : (
                <tr>
                  <th className="p-4 font-medium text-gray-600">Item Name</th>
                  <th className="p-4 font-medium text-gray-600 text-right">Sold Qty</th>
                  <th className="p-4 font-medium text-gray-600 text-right">Returned Qty</th>
                  <th className="p-4 font-medium text-gray-600 text-right">Returned Amount</th>
                </tr>
              )}
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={groupBy === 'Invoice' ? 8 : 4} className="p-8 text-center text-gray-500">
                    Loading data...
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={groupBy === 'Invoice' ? 8 : 4} className="p-8 text-center text-gray-500">
                    No returned invoices found for this period.
                  </td>
                </tr>
              ) : (
                currentData.map((row: any, i: number) => (
                  <tr key={i} className="hover:bg-gray-50/50">
                    {groupBy === 'Invoice' ? (
                      <>
                        <td className="p-4 font-medium text-primary-600">{row.invoice_id}</td>
                        <td className="p-4 text-gray-600 whitespace-nowrap">
                          {formatDateSafe(row.return_date, 'MMM d, yyyy h:mm a')}
                        </td>
                        <td className="p-4 text-gray-600">{row.customer_name || 'Walk in'}</td>
                        <td className="p-4 text-right text-gray-900">{row.total_items}</td>
                        <td className="p-4 text-right text-gray-900">{row.returned_items || 0}</td>
                        <td className="p-4 text-right text-gray-900">
                          {formatMoney(Number(row.total_bill))}
                        </td>
                        <td className="p-4 text-right font-medium text-amber-600">
                          {formatMoney(Number(row.refund_amount))}
                        </td>
                        <td className="p-4 text-right text-gray-900">
                          {formatMoney(Number(row.remaining_amount))}
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="p-4 text-gray-900 font-medium">{row.item_name}</td>
                        <td className="p-4 text-right text-gray-900">{row.sold_qty || 0}</td>
                        <td className="p-4 text-right text-gray-900">{row.returned_qty || 0}</td>
                        <td className="p-4 text-right font-medium text-amber-600">
                          {formatMoney(Number(row.returned_amount))}
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {returnsData && returnsData.length > 0 && (
          <div className="p-4 border-t border-gray-100 bg-gray-50 mt-auto">
            <Pagination page={page} pageSize={LIST_PAGE_SIZE} total={returnsData.length} onPageChange={setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
